/** SLA レポート。承認 24h / タスク 72h の達成率と、超過している行。 */
import { useQuery } from "@tanstack/react-query";
import { Alert, Card, DatePicker, Flex, Space, Statistic, Table, Tag, Typography } from "antd";
import type { ColumnsType } from "antd/es/table";
import dayjs, { type Dayjs } from "dayjs";
import { Link } from "react-router-dom";
import { useState } from "react";

import type { SlaSummary } from "../../lib/api";
import { useApi } from "../../lib/useApi";
import { WF_KINDS } from "./constants";
import { errorText } from "./hooks";

type OverdueRow = SlaSummary["overdue"][number];

export function WfReportPage() {
  const api = useApi();
  const [month, setMonth] = useState<Dayjs | null>(null);
  const key = month ? month.format("YYYY-MM") : "";
  const q = useQuery({
    queryKey: ["hr", "report-summary", key],
    queryFn: () => api.reportSummary(key),
  });

  const columns: ColumnsType<OverdueRow> = [
    {
      title: "種類",
      width: 110,
      render: (_: unknown, r: OverdueRow) => (
        <Tag color={r.kind === "approval" ? "gold" : "blue"}>
          {r.kind === "approval" ? "承認" : "タスク"}
        </Tag>
      ),
    },
    {
      title: "申請",
      width: 160,
      render: (_: unknown, r: OverdueRow) => (
        <Link to={`/hr/wf-requests/${r.request_id}`}>#{r.request_id}</Link>
      ),
    },
    { title: "内容", width: 320, ellipsis: true, dataIndex: "label" },
    { title: "状態", dataIndex: "status", width: 110 },
    {
      title: "経過",
      width: 160,
      render: (_: unknown, r: OverdueRow) => (
        <span>
          {r.elapsed_hours.toFixed(1)}h / {r.limit_hours}h
        </span>
      ),
    },
  ];

  const kinds = Object.entries(q.data?.kinds ?? {});
  const sum = q.data;
  const tasks = sum?.tasks;
  const auto = sum?.automation;
  /** 平均リードタイム(起票 → 完了)。秒で来るので時間に直す。 */
  const lead =
    sum?.lead_time_seconds != null ? `${(sum.lead_time_seconds / 3600).toFixed(1)}h` : "—";

  return (
    <div>
      <Flex align="center" justify="space-between" style={{ marginBottom: 16 }}>
        <Typography.Title level={3} style={{ margin: 0 }}>
          レポート
        </Typography.Title>
        <Space>
          <Typography.Text type="secondary">対象月(JST)</Typography.Text>
          <DatePicker
            picker="month"
            value={month}
            onChange={setMonth}
            placeholder="今月"
            disabledDate={(d) => d.isAfter(dayjs(), "month")}
          />
        </Space>
      </Flex>

      {q.isError && (
        <Alert
          type="error"
          showIcon
          style={{ marginBottom: 16 }}
          message="レポートを取得できなかった"
          description={errorText(q.error)}
        />
      )}

      <Flex gap={16} wrap style={{ marginBottom: 16 }}>
        {kinds.map(([k, v]) => (
          <Card key={k} size="small" style={{ minWidth: 260 }} loading={q.isFetching}>
            <Statistic
              title={`${v.label}(${v.limit_hours}h 以内)`}
              value={v.rate === null ? "—" : `${v.rate.toFixed(1)}%`}
            />
            <Typography.Text type="secondary" style={{ fontSize: 12 }}>
              対象 {v.total} 件 / 完了 {v.done} 件 / 期限内 {v.met} 件 / 未完了 {v.open} 件
            </Typography.Text>
          </Card>
        ))}
      </Flex>

      <Card size="small" title="この月の件数" style={{ marginBottom: 16 }} loading={q.isFetching}>
        <Flex gap={24} wrap>
          <div>
            <Typography.Text strong>申請 {sum?.requests?.total ?? 0} 件</Typography.Text>
            <div style={{ marginTop: 4 }}>
              {WF_KINDS.map((k) => (
                <Tag key={k.value}>
                  {sum?.request_labels?.[k.value] ?? k.label} {sum?.requests?.[k.value] ?? 0}
                </Tag>
              ))}
            </div>
          </div>
          <div>
            <Typography.Text strong>タスク {tasks?.total ?? 0} 件</Typography.Text>
            <div style={{ marginTop: 4 }}>
              <Tag color="green">完了 {tasks?.done ?? 0}</Tag>
              <Tag color="gold">未完了 {tasks?.pending ?? 0}</Tag>
              <Tag color="red">失敗 {tasks?.failed ?? 0}</Tag>
            </div>
          </div>
          <div>
            <Typography.Text strong>
              自動化率 {auto?.rate === null || auto?.rate === undefined ? "—" : `${auto.rate}%`}
            </Typography.Text>
            <div style={{ marginTop: 4 }}>
              <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                完了 {auto?.done ?? 0} 件のうち自動 {auto?.auto_done ?? 0} 件 / 平均リードタイム{" "}
                {lead}
              </Typography.Text>
            </div>
          </div>
          <div>
            <Typography.Text strong>棚卸</Typography.Text>
            <div style={{ marginTop: 4 }}>
              <Link to="/hr/monthly">月次人員棚卸を見る</Link>
            </div>
          </div>
        </Flex>
      </Card>

      <Card
        size="small"
        title={`SLA 超過(${q.data?.overdue_count ?? 0} 件)`}
        extra={
          <Typography.Text type="secondary" style={{ fontSize: 12 }}>
            {q.data?.month ? `${q.data.month} の起票分` : ""}
          </Typography.Text>
        }
      >
        <Table<OverdueRow>
          scroll={{ x: "max-content" }}
          rowKey={(r) => `${r.kind}-${r.id}`}
          size="small"
          columns={columns}
          dataSource={q.data?.overdue ?? []}
          loading={q.isFetching}
          pagination={false}
          locale={{ emptyText: "超過している承認・タスクは無い" }}
        />
      </Card>
    </div>
  );
}
